import { z } from 'zod';

/**
 * Boundary schema for `GET /analysis/:id/statista-datasets`.
 *
 * The generated client types every bare-nullable field on `StatistaDatasetDto`
 * as `{ [key: string]: unknown } | null` — the backend annotates them
 * `@ApiProperty({ nullable: true })` with no `type`, so the OpenAPI spec
 * carries no type to generate from. Parsing here restores the real ones.
 *
 * Every optional field degrades to `null` rather than failing the parse: a
 * dataset whose `deepLink` arrives as an unexpected shape still renders its
 * chart, minus the link. Only `id` and the fields a card cannot be drawn
 * without are required.
 */

/** Coerces anything that is not a usable string to `null`, so one odd field never fails the row. */
const nullableString = z.unknown().transform((value) => (typeof value === 'string' ? value : null));

const nullableBoolean = z
  .unknown()
  .transform((value) => (typeof value === 'boolean' ? value : null));

const nullableNumber = z
  .unknown()
  .transform((value) => (typeof value === 'number' && Number.isFinite(value) ? value : null));

// eslint-disable-next-line i18next/no-literal-string -- wire enum values, never rendered
export const STATISTA_DATA_KINDS = ['primary_statistic', 'model_forecast', 'survey'] as const;

export const StatistaDataKindSchema = z.enum(STATISTA_DATA_KINDS);

export const StatistaTeaserImageSchema = z.object({
  width: z.number(),
  src: z.string(),
});

/**
 * One flattened chart row. The backend normalises all three Statista endpoints
 * to this shape, so a reader of it does not care which one the row came from.
 * `series` names the measure and its unit as one phrase, or the year when a
 * dataset spans several.
 */
export const StatistaNormalizedRowSchema = z.object({
  label: z.string(),
  // A datapoint arrives as a string, an int or a float, and the pull keeps all three rather
  // than coercing. Requiring a number here would drop the whole row - its label and series
  // with it - over a field nothing reads yet.
  value: z.union([z.string(), z.number()]),
  series: z.string().nullish().catch(null),
});

export const StatistaDatasetSchema = z.object({
  id: z.string(),
  templateSlot: z.string(),
  block: z.enum(['A', 'B', 'C']),
  /**
   * Load-bearing: a forecast must never read as measured, so an unrecognised
   * kind fails the row rather than defaulting to one of the three labels.
   */
  dataKind: StatistaDataKindSchema,
  title: z.string(),
  unit: nullableString,
  graphType: nullableString,
  sourceNames: z.array(z.string()).nullish().catch(null),
  publishedDate: nullableString,
  deepLink: nullableString,
  isPremium: nullableBoolean,
  respondentBase: nullableNumber,
  smallBaseFlag: nullableBoolean,
  imageUrl: nullableString,
  teaserImageUrls: z.array(StatistaTeaserImageSchema).catch([]),
  /** Unreadable rows are dropped individually; the rest of the dataset survives. */
  normalized: z
    .array(z.unknown())
    .catch([])
    .transform((rows) =>
      rows.flatMap((row) => {
        const parsed = StatistaNormalizedRowSchema.safeParse(row);
        return parsed.success ? [parsed.data] : [];
      }),
    ),
});

/**
 * Rows are parsed one at a time and the unparseable ones dropped, so a single
 * malformed dataset costs its own card instead of the whole section.
 */
export const StatistaDatasetsResponseSchema = z.object({
  data: z.array(z.unknown()).catch([]),
});

export type StatistaDataKind = z.infer<typeof StatistaDataKindSchema>;
export type StatistaTeaserImage = z.infer<typeof StatistaTeaserImageSchema>;
export type StatistaNormalizedRow = z.infer<typeof StatistaNormalizedRowSchema>;
export type StatistaDataset = z.infer<typeof StatistaDatasetSchema>;

/** Parses the envelope, keeping only the rows that survive their own parse. */
export const parseStatistaDatasets = (payload: unknown): StatistaDataset[] => {
  const envelope = StatistaDatasetsResponseSchema.safeParse(payload);
  if (!envelope.success) return [];

  return envelope.data.data.flatMap((row) => {
    const parsed = StatistaDatasetSchema.safeParse(row);
    return parsed.success ? [parsed.data] : [];
  });
};
